import { useEffect } from 'react';
import { isUUID } from 'validator';
import { useNavigate } from 'react-router-dom';

import { ServerStateProvider } from '@/shared/components';

import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';
import { FOVOverlayProvider } from '@/features/ses/context/FOVOverlayContext';

import ScoreboardData from './ScoreboardData';
import ViewingToolsMenu from './ViewingToolsMenu';
import GamePageContents from './GamePageContents';
import LoadingGame from './LoadingGame';
import useGameSwitchQuery from '../hooks/useGameSwitchQuery';

import { CurrentGameProvider, useCurrentGame } from '../context/CurrentGameContext';
import { SportEventProvider } from '@/features/ses';
import AppPageWithSideContent from '@/shared/components/PageComponents/AppPageWithSideContent';

// Component that consumes the CurrentGameContext
const GamePageContent = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const gameId = useCurrentGameId();
  const { game, refetch, gameLoaded } = useCurrentGame();
  const switchGameQuery = useGameSwitchQuery();

  useDocumentTitle(`${game?.name || 'Game'}`);

  useEffect(() => {
    if (auth.sessionState === LocalSessionState.READY) {
      if (gameId && isUUID(gameId)) {
        refetch();
      } else {
        navigate('/games');
      }
    }
  }, [gameId, auth.sessionState, refetch, navigate]);

  // The switch onto the cloud player (up to a minute) runs behind the page; the
  // dialog only comes back if it fails, for Retry / Back / Session Corrupted.
  if (auth.sessionState !== LocalSessionState.READY || switchGameQuery.isError) {
    return <LoadingGame />;
  }

  if (!gameLoaded) return null;

  return (
    <FOVOverlayProvider>
      <ServerStateProvider gameId={game?.uniqueId}>
        <SportEventProvider gameId={game?.uniqueId}>
          <AppPageWithSideContent
            title={game?.name || 'Loading...'}
            sideContent={() => <ViewingToolsMenu />}
            renderSummaryData={() => <ScoreboardData data={game} />}
          >
            <GamePageContents game={game} />
          </AppPageWithSideContent>
        </SportEventProvider>
      </ServerStateProvider>
    </FOVOverlayProvider>
  );
};

// Main GamePage component that sets up the context provider
const GamePage = () => {
  return (
    <CurrentGameProvider>
      <GamePageContent />
    </CurrentGameProvider>
  );
};

export default GamePage;
